import {
  Directive,
  inject,
  input,
  TemplateRef,
  ViewContainerRef,
  effect,
} from "@angular/core";
import { AuthorizationService } from "../services/authorization.service";
import { UserRole } from "../interfaces/user.interface";

/**
 * Structural directive to show/hide elements based on user role (or higher)
 *
 * Usage:
 * ```html
 * <button *appRequireRole="'PROJECT_MANAGER'">PM or higher</button>
 * <div *appRequireRole="'ADMIN'">Admin only</div>
 * ```
 *
 * The directive checks if user has the specified role OR HIGHER in hierarchy
 * Hierarchy: EMPLOYEE < PROJECT_MANAGER < ACCOUNTANT < ADMIN
 */
@Directive({
  selector: "[appRequireRole]",
  standalone: true,
})
export class RequireRoleDirective {
  private readonly _authz = inject(AuthorizationService);
  private readonly _templateRef = inject(TemplateRef<unknown>);
  private readonly _viewContainer = inject(ViewContainerRef);

  // Use input signal for reactivity
  readonly appRequireRole = input.required<UserRole>();

  private _hasView = false;

  constructor() {
    // Effect runs whenever role input or user role changes
    effect(() => {
      const role = this.appRequireRole();
      const hasRole = this._authz.hasRoleOrHigher()(role);
      this._updateView(hasRole);
    });
  }

  private _updateView(hasRole: boolean): void {
    if (hasRole && !this._hasView) {
      this._viewContainer.createEmbeddedView(this._templateRef);
      this._hasView = true;
    } else if (!hasRole && this._hasView) {
      this._viewContainer.clear();
      this._hasView = false;
    }
  }
}
